import { HttpMethods, makeRequest } from '../helpers/http.helper';
import type { MonthPickupOrders, PickupOrderListItem, PickupOrderPage } from '../types/pickupOrder.type';
import { monthOrderRange } from '../utils/dailyMaterials.helpers';

interface Envelope<T> {
  result?: T;
}

/** The API's page size limit for `GET /pickup_order`. */
const PAGE_SIZE = 100;
/** Pages fetched at most (5,000 orders) — beyond that the split says it is partial. */
const MAX_PAGES = 50;
const PARALLEL_PAGES = 4;

const fetchPage = async (
  organizationId: string,
  range: { start: string; end: string } | null,
  page: number,
): Promise<PickupOrderPage> => {
  const res = (await makeRequest({
    url: '/pickup_order',
    method: HttpMethods.GET,
    params: {
      organization_id: organizationId,
      ...(range ? { start_date: range.start, end_date: range.end } : {}),
      page,
      limit: PAGE_SIZE,
      // A unique sort key keeps offset paging stable when orders share a timestamp.
      sort_by: 'id',
      sort_pickup_order: 'asc',
    },
  })) as Envelope<PickupOrderPage>;
  const result = res?.result;
  return {
    pickup_orders: Array.isArray(result?.pickup_orders) ? result.pickup_orders : [],
    total: Number(result?.total ?? 0),
    page,
    limit: PAGE_SIZE,
  };
};

/**
 * Every pickup order of the organisation created in the month
 * (`pickup_order.list`) — the same set the dashboard's daily totals count.
 * A null month lists the organisation's whole history.
 */
export const listPickupOrdersForMonth = async (organizationId: string, monthStart: Date | null): Promise<MonthPickupOrders> => {
  const range = monthStart ? monthOrderRange(monthStart) : null;
  const first = await fetchPage(organizationId, range, 1);
  const total = first.total;
  const pages = Math.min(Math.ceil(total / PAGE_SIZE), MAX_PAGES);
  const orders: PickupOrderListItem[] = [...first.pickup_orders];
  for (let page = 2; page <= pages; page += PARALLEL_PAGES) {
    const batch = Array.from({ length: Math.min(PARALLEL_PAGES, pages - page + 1) }, (_, i) =>
      fetchPage(organizationId, range, page + i),
    );
    for (const result of await Promise.all(batch)) orders.push(...result.pickup_orders);
  }
  return { orders, total, truncated: total > pages * PAGE_SIZE };
};
